type AgentsGranularRole =
  | "agent_viewer"
  | "agent_editor"
  | "agent_execute_sandbox"
  | "agent_execute_live"
  | "agent_deployer";

type ConnectionsGranularRole =
  | "connection_viewer"
  | "connection_user"
  | "connection_editor";

type DecisionHistoryGranularRole = "dh_viewer_sandbox" | "dh_viewer_live";

type DataAndCasesGranularRole =
  | "case_reviewer"
  | "case_editor"
  | "case_admin"
  | "file_user"
  | "data_viewer"
  | "data_editor"
  | "data_admin";

export type FolderGranularRole =
  | "flow_viewer"
  | "flow_execute_sandbox"
  | "flow_execute_live"
  | "flow_editor"
  | "flow_deployer";

type GranularRoleKeyByVertical = {
  agents: AgentsGranularRole;
  connections: ConnectionsGranularRole;
  decision_history: DecisionHistoryGranularRole;
  data_and_cases: DataAndCasesGranularRole;
  all_folders: FolderGranularRole;
  folder: FolderGranularRole;
};

export type VerticalDependencyKey = keyof GranularRoleKeyByVertical;

export type GranularRoleKey = GranularRoleKeyByVertical[VerticalDependencyKey];

export type GranularRole<K extends GranularRoleKey = GranularRoleKey> = {
  key: K;
  label: string;
};

export type GranularRoleVertical = {
  key: Exclude<VerticalDependencyKey, "folder">;
  label: string;
  granularRoles: GranularRole[];
};

export const FOLDER_GRANULAR_ROLES: GranularRole[] = [
  { key: "flow_viewer", label: "View" },
  { key: "flow_editor", label: "Edit" },
  { key: "flow_execute_sandbox", label: "Use (Test/Sandbox)" },
  { key: "flow_execute_live", label: "Use (Live)" },
  { key: "flow_deployer", label: "Deploy" },
];

export const LOGIC_ALL_FOLDERS_VERTICAL: GranularRoleVertical = {
  key: "all_folders",
  label: "Logic (All Folders)",
  granularRoles: FOLDER_GRANULAR_ROLES,
};

export const WORKSPACE_VERTICALS: GranularRoleVertical[] = [
  {
    key: "agents",
    label: "Agents",
    granularRoles: [
      { key: "agent_viewer", label: "View" },
      { key: "agent_editor", label: "Edit" },
      { key: "agent_deployer", label: "Deploy" },
      { key: "agent_execute_sandbox", label: "Use (Sandbox)" },
      { key: "agent_execute_live", label: "Use (Live)" },
    ],
  },
  {
    key: "connections",
    label: "Connections",
    granularRoles: [
      { key: "connection_viewer", label: "View" },
      { key: "connection_editor", label: "Edit/Deploy" },
      { key: "connection_user", label: "Use (Live/Sandbox)" },
    ],
  },
  {
    key: "decision_history",
    label: "Decision History",
    granularRoles: [
      { key: "dh_viewer_sandbox", label: "View (Sandbox)" },
      { key: "dh_viewer_live", label: "View (Live)" },
    ],
  },
  {
    key: "data_and_cases",
    label: "Data & Cases",
    granularRoles: [
      { key: "data_viewer", label: "Data Viewer" },
      { key: "data_editor", label: "Data Editor" },
      { key: "data_admin", label: "Data Admin" },
      { key: "case_reviewer", label: "Case Reviewer" },
      { key: "case_editor", label: "Case Editor" },
      { key: "case_admin", label: "Case Admin" },
      { key: "file_user", label: "File User" },
    ],
  },
];

export const FOLDER_DEPENDENCY_KEY = "folder";

export const WORKSPACE_ADMIN_ROLE = "ws_admin";
export const WORKSPACE_EDITOR_ROLE = "ws_editor";
export const WORKSPACE_REVIEWER_ROLE = "ws_reviewer";
export const WORKSPACE_MEMBER_ROLE = "ws_viewer";

/** Every workspace role a user can hold, including the two the dropdown cannot set. */
export const WORKSPACE_ROLES: string[] = [
  WORKSPACE_ADMIN_ROLE,
  WORKSPACE_EDITOR_ROLE,
  WORKSPACE_REVIEWER_ROLE,
  WORKSPACE_MEMBER_ROLE,
];

/** Not a role: the option that revokes the workspace role of the user. */
export const NO_WORKSPACE_ROLE = "none";

// The values taktile-auth grants use for "every one" and "the one with no id".
export const WILDCARD_ARGUMENT = "*";
export const NO_FOLDER_ARGUMENT = "null";

const DEPENDENCY_KEY_BY_GRANULAR_ROLE = new Map<
  GranularRoleKey,
  VerticalDependencyKey
>([
  ...WORKSPACE_VERTICALS.flatMap((vertical) =>
    vertical.granularRoles.map(
      (granularRole): [GranularRoleKey, VerticalDependencyKey] => [
        granularRole.key,
        vertical.key,
      ],
    ),
  ),
  ...FOLDER_GRANULAR_ROLES.map(
    (granularRole): [GranularRoleKey, VerticalDependencyKey] => [
      granularRole.key,
      FOLDER_DEPENDENCY_KEY,
    ],
  ),
]);

export const ALL_GRANULAR_ROLES: GranularRoleKey[] = [
  ...DEPENDENCY_KEY_BY_GRANULAR_ROLE.keys(),
];

export const getGranularRoleDependencyKey = (
  roleName: string,
): VerticalDependencyKey | undefined =>
  DEPENDENCY_KEY_BY_GRANULAR_ROLE.get(roleName as GranularRoleKey);

type DependencyRules<R extends GranularRoleKey> = Partial<Record<R, R[]>>;

type GranularRoleDependencies = {
  [K in VerticalDependencyKey]: DependencyRules<GranularRoleKeyByVertical[K]>;
};

// verticalKey → granularRoleKey → direct prerequisites; closure computed below.
// Rules mirror the "inherits" relation in the Permission Set - Milestone 1
// Notion database. Verticals with no Milestone-1 equivalent are left empty.
export const GRANULAR_ROLE_DEPENDENCIES: GranularRoleDependencies = {
  agents: {
    agent_editor: ["agent_viewer", "agent_execute_sandbox"],
    agent_deployer: ["agent_editor"],
  },
  connections: {
    connection_editor: ["connection_viewer", "connection_user"],
  },
  decision_history: {},
  data_and_cases: {
    case_admin: ["case_editor"],
    case_editor: ["case_reviewer"],
    data_admin: ["data_editor"],
    data_editor: ["data_viewer"],
  },
  all_folders: {
    flow_deployer: ["flow_editor"],
    flow_editor: ["flow_viewer", "flow_execute_sandbox"],
  },
  [FOLDER_DEPENDENCY_KEY]: {
    flow_deployer: ["flow_editor"],
    flow_editor: ["flow_viewer", "flow_execute_sandbox"],
  },
};

export const getRequiredGranularRoles = <K extends VerticalDependencyKey>(
  verticalKey: K,
  granularRoleKey: GranularRoleKeyByVertical[K],
  seen: ReadonlySet<GranularRoleKeyByVertical[K]> = new Set(),
): GranularRoleKeyByVertical[K][] => {
  if (seen.has(granularRoleKey)) {
    return [];
  }
  const nextSeen = new Set([...seen, granularRoleKey]);
  const rules = GRANULAR_ROLE_DEPENDENCIES[verticalKey] as Partial<
    Record<string, GranularRoleKeyByVertical[K][]>
  >;
  const direct = rules[granularRoleKey] ?? [];
  const transitive = direct.flatMap((key) =>
    getRequiredGranularRoles(verticalKey, key, nextSeen),
  );
  return [...new Set([...direct, ...transitive])];
};

export const getDependentGranularRoles = <K extends VerticalDependencyKey>(
  verticalKey: K,
  granularRoleKey: GranularRoleKeyByVertical[K],
): GranularRoleKeyByVertical[K][] =>
  (
    Object.keys(
      GRANULAR_ROLE_DEPENDENCIES[verticalKey],
    ) as GranularRoleKeyByVertical[K][]
  ).filter((candidate) =>
    getRequiredGranularRoles(verticalKey, candidate).includes(granularRoleKey),
  );

export const DUMMY_KEY_COUNTS: Record<string, number> = {
  agents: 0,
  connections: 1,
  decision_history: 2,
  data_and_cases: 2,
  all_folders: 0,
};

// Placeholder used/total permission slots until DEC-9983 wires the backend
export const DUMMY_PERMISSION_SLOTS: { used: number; total: number } = {
  used: 8,
  total: 100,
};
